import { UserAuthToken } from '~/models/AuthToken'
import { AuthService } from '~/passporter-services/auth/service'
import { ProfileService } from '~/passporter-services/profile/service'
import CookieService from '~/services/cookies'
import EventsService from '~/services/events'
import { AuthNavigation } from '~/services/navigation/authNavigation'
import { BaseNavigation } from '~/services/navigation/baseNavigation'
import { HomeNavigation } from '~/services/navigation/home'
import { TripNavigation } from '~/services/navigation/trip'
import { GoogleOauthService } from '~/services/oauth/googleOAuth'
import { TranslationService } from '~/services/Translation'
import { clearAllCaches } from '~/services/cacheRegistry'
import { useAuthStore } from '~/store/auth'
import { useProfileStore } from '~/store/profile'
import { LeadActions } from '~/actions/lead/action'
import { TripTravelerActions } from '~/actions/trip/traveler'
import { useProfileTrips } from '~/store/profileTrips'
import {
	DialogController,
	PredefinedDialog,
	PredefinedDialogType,
} from '~/components/passporterUi/Dialog/dialogController'
import { RegisterRequireDialog } from '#components'
import { Profile } from '~/models/Profile'
import { RegistrationMetric } from '~/actions/metrics/registration'
import { TripLeadActions } from '~/actions/trip/lead'
import { MixpanelService } from '~/services/mixpanel'

export class AuthActions {
	static createRegisterRequireDialog(isXS: boolean): DialogController {
		const dialogSetup = new PredefinedDialog(PredefinedDialogType.EMPTY, RegisterRequireDialog)
			.props({ isXS })
			.size('s')
			.mobileType('bottomSheet')
			.closeOnBackdropClick(false)
			.build()

		return new DialogController(dialogSetup)
	}

	static async loginWithPassword(email: string, password: string): Promise<boolean> {
		try {
			const authData = await AuthService.loginWithPassword(email, password)
			await this.continue(authData)
			return true
		} catch (e) {
			this.manageError(e)
			return false
		}
	}

	static async register(email: string, password: string): Promise<boolean> {
		const t = TranslationService.getInstance()
		try {
			const registerUrl = CookieService.getRegisterMetricsUrl()
			await AuthService.register(email, password, registerUrl)
			CookieService.deleteRegisterMetricsUrl()
			EventsService.emitSuccess(t.translate('common_successful_register'))
			return true
		} catch (e: any) {
			this.manageError(e)
			return false
		}
	}

	static async logout(): Promise<void> {
		try {
			CookieService.deleteCookie('authentication-cookie')
			MixpanelService.reset()
			await AuthService.logout()
			AuthNavigation.toLogin()
			this.profileStore.resetAll()
			this.profileTripsStore.resetAll()
			clearAllCaches()
		} catch (e) {
			this.manageError(e)
		}
	}

	static async requestPasswordReset(email: string): Promise<void> {
		try {
			await AuthService.requestPasswordReset(email)
			const t = TranslationService.getInstance()
			EventsService.emitSuccess(t.translate('Common_recovery_success_message'))
		} catch (e) {
			this.manageError(e)
		}
	}

	static async restorePassword(code: string, email: string, password: string): Promise<void> {
		try {
			await AuthService.restorePassword(code, email, password)
			const t = TranslationService.getInstance()
			EventsService.emitSuccess(t.translate('Toast_password_succesfully_recovered'))
			AuthNavigation.toLogin()
		} catch (e) {
			this.manageError(e)
		}
	}

	static async requestPasswordlessLogin(email: string): Promise<void> {
		try {
			const registerUrl = CookieService.getRegisterMetricsUrl()
			await AuthService.requestPasswordlessLogin(email, registerUrl)
			CookieService.deleteRegisterMetricsUrl()
		} catch (e) {
			this.manageError(e)
		}
	}

	static async loginWithToken(token: string): Promise<void> {
		try {
			const authData = await AuthService.loginWithToken(token)
			await this.continue(authData)
		} catch (e) {
			this.manageError(e)
		}
	}

	static async loginWithTrip(token: string, tripId: string): Promise<void> {
		try {
			const authData = await AuthService.loginWithToken(token)
			await this.continue(authData, false)
			TripNavigation.toThanksPage(tripId)
		} catch (e) {
			this.manageError(e)
		}
	}

	static async loginWithGoogle(googleToken: string): Promise<boolean> {
		try {
			const registerUrl = CookieService.getRegisterMetricsUrl()
			const authData = await AuthService.loginWithGoogle(googleToken, registerUrl)
			CookieService.deleteRegisterMetricsUrl()
			await this.continue(authData)
			return true
		} catch (e) {
			this.manageError(e)
			return false
		}
	}

	static async initGoogleLogin(
		parentDiv: HTMLDivElement,
		callback: (response: google.accounts.id.CredentialResponse) => void
	) {
		GoogleOauthService.init(callback)
		if (parentDiv) GoogleOauthService.renderButton(parentDiv)
	}

	static redirectToRouteAfterLogin(route: string): void {
		this.authStore.setPreviousRoute(route)
	}

	static isAuthenticated(): boolean {
		const authCookie = CookieService.getCookie('authentication-cookie')
		const accessToken = authCookie?.auth?.accessToken
		return Boolean(accessToken)
	}

	private static async continue(authData: UserAuthToken, redirect: boolean = true) {
		this.setAuthCookie(authData)
		await this.saveProfile(authData.id!)
		MixpanelService.identify(authData.id!)
		const leadId = CookieService.getLeadId()
		const salesLeadId = CookieService.getSalesLeadId()
		if (leadId) return await this.generateTrip(leadId)
		if (salesLeadId) return await this.generateSalesTrip(salesLeadId)
		if (redirect) this.handleRedirect()
	}

	private static setAuthCookie(authData: UserAuthToken) {
		let expirationDate: Date | undefined
		const userId = authData.id as string | undefined
		if (authData.exp) expirationDate = new Date(authData.exp)
		CookieService.setAuthCookie(authData.access, userId, expirationDate)
	}

	private static async saveProfile(userId: string): Promise<void> {
		try {
			const profile = await ProfileService.getProfile(userId)
			this.sendRegistrationCompleteEvent(profile)
			this.profileStore.setProfile(profile)
		} catch (e) {
			console.error(e)
		}
	}

	private static async handleRedirect() {
		const invitation = CookieService.getTripInvitation()

		if (invitation) {
			CookieService.deleteTripInvitation()
			return await TripTravelerActions.acceptInvitation(invitation.tripId, invitation.code, true)
		}
		const previousRoute = this.authStore.getPreviousRoute()
		if (!previousRoute) return HomeNavigation.toHome()
		BaseNavigation.navigateTo(previousRoute)
		this.authStore.resetPreviousRoute()
	}

	static saveLeadId(leadId: string) {
		CookieService.setLeadId(leadId)
	}

	private static async generateTrip(leadId: string): Promise<void> {
		CookieService.deleteLeadId()
		try {
			const itinerary = await LeadActions.createTrip(leadId)
			if (itinerary) return TripNavigation.toTripDashboard(itinerary.id!)
			this.handleRedirect()
		} catch {
			this.handleRedirect()
		}
	}

	private static async generateSalesTrip(leadId: string): Promise<void> {
		try {
			const itinerary = await TripLeadActions.createTrip(leadId)
			if (itinerary?.id) return TripNavigation.toTripDashboard(itinerary.id)
			this.handleRedirect()
		} catch {
			this.handleRedirect()
		}
	}

	private static manageError(error: any) {
		console.error(error)
		const errorDetail = error?.data?.detail
		if (errorDetail) return EventsService.emitError(errorDetail)
		const t = TranslationService.getInstance()
		EventsService.emitError(t.translate('E_Error_unknown_server_response'))
	}

	private static sendRegistrationCompleteEvent(profile: Profile) {
		const assumeUserJustRegister = !profile.birthYear
		if (assumeUserJustRegister) RegistrationMetric.sendRegistrationCompleteEvent()
	}

	static get profileStore() {
		return useProfileStore()
	}
	static get profileTripsStore() {
		return useProfileTrips()
	}
	static get authStore() {
		return useAuthStore()
	}
}
